import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";

import {
  get_all_bates_config,
  update_bates_config,
  delete_bates_config,
} from "../../functions/api";

import Menu from "../navigation/Menu";
import Nav from "../navigation/Nav";

const ALLOWED_VARIABLES = [
  { token: "{rapportnummer}", description: "Rapportnummer (valt terug op identification UUID als leeg)" },
  { token: "{identification}", description: "Identification UUID van de rapportage" },
  { token: "{pagina}", description: "Huidig paginanummer (bijv. 5)" },
  { token: "{pagina:04d}", description: "Paginanummer met zero-padding (bijv. 0005)" },
  { token: "{totaalpaginas}", description: "Totaal aantal pagina's" },
  { token: "{jaar}", description: "Jaar van de inspectie (datumstart)" },
  { token: "{maand}", description: "Maand van de inspectie (2 cijfers)" },
  { token: "{dag}", description: "Dag van de inspectie (2 cijfers)" },
  { token: "{soortinspectie}", description: "Naam van het inspectietype" },
  { token: "{klant}", description: "Klant bedrijfsnaam (valt terug op klantnaam)" },
  { token: "{objectnummer}", description: "Object nummer" },
  { token: "{postcode}", description: "Postcode van de locatie" },
];

const SAMPLE_VARS = {
  rapportnummer: "1234",
  identification: "abc123def456",
  pagina: 5,
  totaalpaginas: 42,
  jaar: "2026",
  maand: "05",
  dag: "07",
  soortinspectie: "Elektra-keuring",
  klant: "ACME BV",
  objectnummer: "OBJ-42",
  postcode: "1234AB",
};

function renderPreview(pattern) {
  if (!pattern) return "";
  return pattern.replace(/\{(\w+)(?::([^}]+))?\}/g, (_match, name, formatter) => {
    if (!(name in SAMPLE_VARS)) return "";
    const value = SAMPLE_VARS[name];
    if (formatter && /^0?\d+d$/.test(formatter)) {
      const m = formatter.match(/^0?(\d+)d$/);
      const width = parseInt(m[1], 10);
      return String(value).padStart(width, "0");
    }
    return String(value);
  });
}

function BatesConfigComponent() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const thStyle =
    "px-4 py-3 border-b-2 border-gray-200 bg-gray-50 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider";
  const tdStyle = "px-4 py-3 border-b border-gray-200 bg-white text-sm align-center";

  const loadData = () => {
    setLoading(true);
    get_all_bates_config()
      .then((response) => {
        if (response.ok || response.status < 300) return response.json();
        throw response;
      })
      .then((apiData) => {
        const list = Array.isArray(apiData) ? apiData : apiData.result || [];
        setRows(list);
        const initialDrafts = {};
        list.forEach((r) => {
          const hasOverride = r.account_id !== null && r.account_id !== undefined;
          const effectivePattern = hasOverride
            ? r.account_pattern || ""
            : r.global_pattern || "";
          const effectiveEnabled = hasOverride
            ? r.account_enabled === null || r.account_enabled === undefined
              ? true
              : !!Number(r.account_enabled)
            : r.global_enabled === null || r.global_enabled === undefined
            ? true
            : !!Number(r.global_enabled);
          initialDrafts[r.soortinspectiesid] = {
            pattern: effectivePattern,
            enabled: effectiveEnabled,
            persisted: hasOverride,
            globalPattern: r.global_pattern || "",
          };
        });
        setDrafts(initialDrafts);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching bates configs:", error);
        Swal.fire({
          title: "Fout!",
          text: "Kon Bates-configuraties niet laden.",
          icon: "error",
        });
        setRows([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDraftChange = (typeId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [typeId]: { ...prev[typeId], [field]: value },
    }));
  };

  const handleSave = (typeId) => {
    const draft = drafts[typeId];
    if (!draft) return;
    if (draft.enabled && !draft.pattern.trim()) {
      Swal.fire({
        title: "Pattern vereist",
        text: "Vul een pattern in of zet de Bates uit voor dit type.",
        icon: "warning",
      });
      return;
    }
    setSaving((prev) => ({ ...prev, [typeId]: true }));
    update_bates_config(typeId, {
      pattern: draft.pattern.trim(),
      enabled: draft.enabled,
    })
      .then((response) => {
        if (response.ok || response.status < 300) return response.json();
        return response.json().then((body) => {
          throw body;
        });
      })
      .then(() => {
        Swal.fire({
          title: "Opgeslagen",
          text: "Bates-configuratie bijgewerkt.",
          icon: "success",
          timer: 1200,
          showConfirmButton: false,
        });
        loadData();
      })
      .catch((error) => {
        console.error("Error saving bates config:", error);
        const msg =
          (error && error.error) ||
          (error && error.details && error.details.join(", ")) ||
          "Opslaan mislukt.";
        Swal.fire({ title: "Fout!", text: msg, icon: "error" });
      })
      .finally(() => {
        setSaving((prev) => ({ ...prev, [typeId]: false }));
      });
  };

  const handleReset = (typeId) => {
    const swalWithButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success mx-2",
        cancelButton: "btn btn-danger mx-2",
      },
      buttonsStyling: false,
    });
    swalWithButtons
      .fire({
        title: "Terugzetten naar standaard?",
        text: "De override voor dit inspectietype wordt verwijderd; het type valt terug op het standaard pattern.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ja, verwijder override",
        cancelButtonText: "Annuleren",
        reverseButtons: true,
      })
      .then((result) => {
        if (!result.isConfirmed) return;
        delete_bates_config(typeId)
          .then((response) => {
            if (response.ok || response.status < 300) return response.json();
            throw response;
          })
          .then(() => {
            Swal.fire({
              title: "Verwijderd",
              icon: "success",
              timer: 1200,
              showConfirmButton: false,
            });
            loadData();
          })
          .catch((error) => {
            console.error("Error deleting bates config:", error);
            Swal.fire({ title: "Fout!", text: "Verwijderen mislukt.", icon: "error" });
          });
      });
  };

  return (
    <div className="select-report-container-main">
      <Nav page="Bates-nummering" />
      <div className="page-wrap-row">
        <Menu className="menu-container" />
        <div className="select-report-container p-6 md:p-10">
          <h1 className="text-2xl font-semibold mb-2 ml-10 mt-10">
            Bates-nummering per inspectietype
          </h1>
          <p className="ml-10 mb-6 text-gray-600 text-sm max-w-3xl">
            Stel per inspectietype een patroon in voor de Bates-stempel in de
            footer van rapporten. Wijzigingen gelden alleen voor jouw eigen
            account. Inspectietypes zonder eigen override gebruiken de globale
            standaard. "Terug naar standaard" verwijdert je eigen override.
          </p>

          <div className="px-10 mb-6">
            <details className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <summary className="font-semibold text-blue-900 cursor-pointer">
                Beschikbare variabelen
              </summary>
              <table className="mt-3 w-full text-sm">
                <tbody>
                  {ALLOWED_VARIABLES.map((v) => (
                    <tr key={v.token} className="border-b border-blue-100 last:border-0">
                      <td className="py-1 pr-4 font-mono text-blue-900 whitespace-nowrap">
                        {v.token}
                      </td>
                      <td className="py-1 text-gray-700">{v.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-gray-600">
                Voorbeeld:{" "}
                <code className="bg-white px-1 rounded">
                  {"EL-{jaar}/{rapportnummer}-{pagina:04d}"}
                </code>{" "}
                → <code className="bg-white px-1 rounded">EL-2026/2026-001-0005</code>
              </p>
            </details>
          </div>

          <div className="select-report-container-1">
            <div className="overflow-x-auto shadow rounded-lg">
              <table className="min-w-full leading-normal">
                <thead>
                  <tr>
                    <th className={thStyle}>Inspectietype</th>
                    <th className={thStyle}>Patroon</th>
                    <th className={thStyle}>Voorbeeld</th>
                    <th className={thStyle}>Ingeschakeld</th>
                    <th className={`${thStyle} text-right`}>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="6" className={`${tdStyle} text-center`}>
                        Laden...
                      </td>
                    </tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan="6" className={`${tdStyle} text-center`}>
                        Geen inspectietypes gevonden.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    rows.map((row) => {
                      const typeId = row.soortinspectiesid;
                      const draft = drafts[typeId] || {
                        pattern: "",
                        enabled: true,
                        persisted: false,
                        globalPattern: row.global_pattern || "",
                      };
                      return (
                        <tr key={typeId} className="hover:bg-gray-50">
                          <td className={tdStyle}>
                            <div className="font-medium">{row.soortnaam || `Type ${typeId}`}</div>
                          </td>
                          <td className={tdStyle}>
                            <input
                              type="text"
                              value={draft.pattern}
                              maxLength={255}
                              onChange={(e) =>
                                handleDraftChange(typeId, "pattern", e.target.value)
                              }
                              placeholder="{rapportnummer}-{pagina:04d}"
                              className="w-full px-2 py-1 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </td>
                          <td className={`${tdStyle} font-mono text-sm`}>
                            {renderPreview(draft.pattern) || (
                              <span className="text-gray-400 italic">leeg</span>
                            )}
                          </td>
                          <td className={`${tdStyle} text-center`}>
                            <input
                              type="checkbox"
                              checked={draft.enabled}
                              onChange={(e) =>
                                handleDraftChange(typeId, "enabled", e.target.checked)
                              }
                              className="h-4 w-4"
                            />
                          </td>
                          <td className={`${tdStyle} text-right whitespace-nowrap`}>
                            <button
                              onClick={() => handleSave(typeId)}
                              disabled={saving[typeId]}
                              className="px-3 py-1.5 rounded text-white text-sm mr-2 disabled:opacity-50"
                              style={{ backgroundColor: "#060a24" }}
                            >
                              {saving[typeId] ? "Opslaan..." : "Opslaan"}
                            </button>
                            {draft.persisted && (
                              <button
                                onClick={() => handleReset(typeId)}
                                className="px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100"
                              >
                                Terug naar standaard
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatesConfigComponent;
